import CommonTable from '../../../../components/Shared/Tables/TableV1.tsx';
import useTemplateSx from '../../hooks/useTemplateSx';

const normalizeStatus = (status = '') => {
  const normalizedStatus = String(status).trim().toUpperCase();

  if (
    [
      'PENDING_DELETION',
      'PENDING',
      'ACCEPTED',
      'MD_PENDING',
    ].includes(normalizedStatus)
  ) {
    return 'Pending';
  }

  if (normalizedStatus === 'APPROVED') {
    return 'Approved';
  }

  if (normalizedStatus === 'REJECTED') {
    return 'Rejected';
  }

  return status || '-';
};

export default function TemplateDataTable({
  muiTableHeadCellProps,
  muiTableHeadRowProps,
  muiTableBodyCellProps,
  muiTableBodyRowProps,
  muiTablePaperProps,
  muiTableContainerProps,
  muiBottomToolbarProps,
  layoutMode = 'semantic',
  enableColumnPinning = false,
  rowSelection,
  onRowSelectionChange,
  enableRowSelection,
  enableMultiRowSelection = true,
  enableSelectAll = true,
  isLoading = false,
  sorting,
  state,
  ...props
}) {
  const styles = useTemplateSx();

  const normalizedData = (props.data || []).map((row) => ({
    ...row,
    status: normalizeStatus(row.status),
  }));

  const activeRowSelection = rowSelection ?? state?.rowSelection ?? {};

  const tableState = {
    isLoading: isLoading ?? state?.isLoading ?? false,
    sorting: sorting ?? state?.sorting ?? [],
    rowSelection: activeRowSelection,
    ...(state || {}),
  };

  return (
    <CommonTable
      {...props}
      data={normalizedData}
      layoutMode={layoutMode}
      enableColumnPinning={enableColumnPinning}
      enableRowSelection={enableRowSelection}
      onRowSelectionChange={onRowSelectionChange}
      enableMultiRowSelection={enableMultiRowSelection}
      enableSelectAll={enableSelectAll}
      state={tableState}
      muiTablePaperProps={{
        elevation: 0,
        ...muiTablePaperProps,
        sx: {
          ...styles.tablePaper,
          ...(muiTablePaperProps?.sx ?? {}),
        },
      }}
      muiTableContainerProps={{
        ...muiTableContainerProps,
        sx: {
          ...styles.tableContainer,
          ...(muiTableContainerProps?.sx ?? {}),
        },
      }}
      muiTableHeadRowProps={{
        ...muiTableHeadRowProps,
        sx: {
          ...styles.tableHeadRow,
          ...(muiTableHeadRowProps?.sx ?? {}),
        },
      }}
      muiTableHeadCellProps={{
        ...muiTableHeadCellProps,
        sx: {
          ...styles.tableHeadCell,
          '& .MuiCheckbox-root': {
            color: '#059669',
            '&.Mui-checked': {
              color: '#059669 !important',
            },
            '&.MuiCheckbox-indeterminate': {
              color: '#059669 !important',
            },
          },
          ...(muiTableHeadCellProps?.sx ?? {}),
        },
      }}
      muiTableBodyCellProps={{
        ...muiTableBodyCellProps,
        sx: {
          ...styles.tableBodyCell,
          whiteSpace: 'nowrap',
          '& .MuiCheckbox-root': {
            color: '#059669',
            '&.Mui-checked': {
              color: '#059669 !important',
            },
          },
          ...(muiTableBodyCellProps?.sx ?? {}),
        },
      }}
      muiTableBodyRowProps={(rowProps) => {
        const row = rowProps?.row;
        const isSelected = Boolean(
          row?.getIsSelected?.() || (row?.id && activeRowSelection[row.id])
        );
        const customProps = typeof muiTableBodyRowProps === 'function'
          ? muiTableBodyRowProps(rowProps)
          : (muiTableBodyRowProps ?? {});

        return {
          ...customProps,
          selected: isSelected,
          sx: {
            ...styles.tableBodyRow,
            ...(isSelected ? {
              bgcolor: '#ecfdf5 !important',
              '& td': {
                bgcolor: '#ecfdf5 !important',
              },
            } : {}),
            '&.Mui-selected, &.Mui-selected:hover': {
              bgcolor: '#ecfdf5 !important',
              '& td': {
                bgcolor: '#ecfdf5 !important',
              },
            },
            ...(customProps?.sx ?? {}),
          },
        };
      }}
      muiBottomToolbarProps={{
        ...muiBottomToolbarProps,
        sx: {
          bgcolor: 'transparent',
          boxShadow: 'none',
          ...(muiBottomToolbarProps?.sx ?? {}),
        },
      }}
    />
  );
}
